import type { ReactNode } from "react";
import { Loader2, AlertTriangle, Inbox } from "lucide-react";
import { Card } from "@/components/ui/card";
import { CardTitle, Subtitle } from "@/components/ui/typography";
import { cn } from "@/components/ui/cn";

type LoadingStateProps = {
  label?: string;
  className?: string;
};

export function LoadingState({
  label = "Laden…",
  className,
}: LoadingStateProps): React.JSX.Element {
  return (
    <div
      className={cn("flex items-center justify-center gap-2 p-6 text-muted-foreground", className)}
      role="status"
      aria-live="polite"
    >
      <Loader2 className="size-4 animate-spin" aria-hidden />
      <span className="text-sm">{label}</span>
    </div>
  );
}

type ErrorMessageProps = {
  title?: string;
  message?: string;
  action?: ReactNode;
  className?: string;
};

export function ErrorMessage({
  title = "Er ging iets mis",
  message = "Probeer het later opnieuw.",
  action,
  className,
}: ErrorMessageProps): React.JSX.Element {
  return (
    <Card padding="lg" className={cn("border-error-200 bg-error-50", className)}>
      <div className="flex items-start gap-3">
        <AlertTriangle className="size-5 text-error-600" aria-hidden />
        <div className="space-y-1">
          <CardTitle className="text-error-800">{title}</CardTitle>
          <Subtitle className="text-error-700">{message}</Subtitle>
          {action ? <div className="pt-2">{action}</div> : null}
        </div>
      </div>
    </Card>
  );
}

type EmptyStateProps = {
  icon?: ReactNode;
  title: string;
  description?: string;
  action?: ReactNode;
  className?: string;
};

export function EmptyState({
  icon,
  title,
  description,
  action,
  className,
}: EmptyStateProps): React.JSX.Element {
  return (
    <div
      className={cn(
        "rounded-xl border border-dashed border-neutral-300 px-6 py-10 text-center",
        className,
      )}
    >
      <div className="flex flex-col items-center gap-1.5">
        <div className="mb-2 flex size-11 items-center justify-center rounded-full bg-muted text-muted-foreground">
          {icon ?? <Inbox className="size-5" aria-hidden />}
        </div>
        <CardTitle className="text-sm">{title}</CardTitle>
        {description ? <Subtitle className="text-xs">{description}</Subtitle> : null}
        {action ? <div className="pt-3">{action}</div> : null}
      </div>
    </div>
  );
}
